<template>
  <div class="snow-data-table">
    <div v-if="loading" class="text-center py-9">
      <b-spinner variant="primary" />
    </div>

    <div v-else-if="!data.length" class="d-flex justify-content-center w-100">
      <div class="no-content">
        <h2 class="text-center text-semibold ">
          Ainda não há nada aqui
        </h2>
        <img
          class="d-block mx-auto mt-4"
          src="@/assets/images/no-content.svg"
        />
        <template v-if="$slots['no-content-actions']">
          <hr class="w-100" />
          <h2 class="mb-4">Talvez você precise</h2>
          <div class="d-flex justify-content-center w-100">
            <slot name="no-content-actions" />
          </div>
        </template>
      </div>
    </div>

    <template v-else>
      <div v-if="isDesktop" class="snow-desktop-table" :class="tableClasses">
        <div
          :style="tableStyle"
          class="snow-desktop-table border-bottom border-dark-20"
        >
          <div v-for="(column, index) in columns" :key="column.name">
            <slot :name="'header-' + getSlotName(column, index)">
              <div class="p-3 text-dark-40 fs-12">
                {{ column.name }}
              </div>
            </slot>
          </div>
        </div>

        <div
          v-for="(item, i) in data"
          class="snow-desktop-table table-row border-bottom border-dark-4"
          :key="i"
          :style="tableStyle"
        >
          <div
            v-for="(column, cidx) in columns"
            :key="column.name"
            class="py-1 px-3"
          >
            <snow-data-table-dropdown
              v-if="
                column.type === 'menu' && getOptions(item, column).length > 0
              "
              :item="item"
              :options="getOptions(item, column)"
              :disabled="isDisabled(item, column)"
              @click="clickOption"
            />
            <slot
              v-else-if="column.type !== 'menu'"
              :item="item"
              :value="getFieldValue(item, column)"
              :name="getSlotName(column, cidx)"
            >
              <snow-data-table-field :item="item" :column="column" />
            </slot>
          </div>
        </div>
      </div>

      <div v-else class="snow-responsive-table">
        <snow-select
          v-model="selectedCategory"
          size="md"
          label="Visualizar categoria"
          :options="categories"
        />

        <div
          v-for="(item, i) in data"
          class="border-neutral-076"
          :key="i"
          :class="currentIndex !== i && 'border-bottom'"
        >
          <div
            class="snow-responsive-table-data-header py-5"
            @click="toggleRow(i)"
          >
            <slot
              :item="item"
              :name="getSlotName(mainColumn)"
              :value="getFieldValue(item, mainColumn)"
            >
              <snow-data-table-field :column="mainColumn" :item="item" />
            </slot>

            <slot
              :item="item"
              :value="getFieldValue(item, activeColumn)"
              :name="getSlotName(activeColumn)"
            >
              <snow-data-table-field :item="item" :column="activeColumn" />
            </slot>

            <i
              :class="
                currentIndex === i ? 'ph ph-caret-up' : 'ph ph-caret-down'
              "
            />
          </div>

          <template v-if="currentIndex === i">
            <div class="d-flex flex-column gap-3 my-5">
              <div
                class="d-flex justify-content-between align-items-start gap-5"
                v-for="(column, cidx) in columns.filter(c => c.type !== 'menu')"
                :key="column.name || cidx"
              >
                <slot :name="'header-' + getSlotName(column, cidx)">
                  <div class="text-neutral-500">
                    <h4>
                      {{ column.name }}
                    </h4>
                  </div>
                </slot>

                <div class="text-right">
                  <slot
                    :item="item"
                    :value="getFieldValue(item, column)"
                    :name="getSlotName(column, cidx)"
                  >
                    <snow-data-table-field :item="item" :column="column" />
                  </slot>
                </div>
              </div>
            </div>

            <div
              v-if="optionsColumn && getOptions(item, optionsColumn).length > 0"
              class="d-flex flex-column"
            >
              <snow-button
                v-for="(option, index) in getOptions(item, optionsColumn)"
                class="mb-3"
                size="lg"
                :variant="option.variant || 'gray'"
                :key="index"
                :icon="option.icon"
                :text="option.label"
                :disabled="isDisabled(item, optionsColumn)"
                @click="clickOption(option, item)"
              />
            </div>
          </template>
        </div>
      </div>
    </template>

    <snow-dialog
      v-model="dialog"
      v-bind="dialogProps"
      @action="onDialogAction"
      @secondary-action="onDialogSecondaryAction"
    />
  </div>
</template>

<script>
import isMobile from "ismobilejs";
import SnowDataTableDropdown from "./SnowDataTableDropdown.vue";
import SnowDataTableField from "./SnowDataTableField.vue";
export default {
  components: { SnowDataTableDropdown, SnowDataTableField },
  props: {
    data: {
      type: Array,
      default: () => []
    },
    emptyMessage: {
      type: String,
      default: ""
    },
    columns: {
      type: Array,
      default: () => []
    },
    main: {
      type: String,
      default: ""
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedCategory: this.columns[1]?.name || "",
      categories: this.columns
        .filter(c => !!c.name)
        .map(col => ({ id: col.name, name: col.name })),
      currentIndex: undefined,

      dialog: false,
      dialogProps: {},
      dialogItem: undefined,
      dialogAction: undefined,
      dialogSecondaryAction: undefined
    };
  },
  computed: {
    isDesktop() {
      return !isMobile().any;
    },
    tableClasses() {
      const classes = [];

      return classes;
    },
    tableStyle() {
      const columnSizes = this.isDesktop
        ? this.columns
            .map(c => (c.type == "menu" ? "50px" : c.size || "1fr"))
            .join(" ")
        : "1fr auto auto";

      return {
        display: "grid",
        gridTemplateColumns: columnSizes
      };
    },
    optionsColumn() {
      return this.columns.find(c => c.type == "menu");
    },
    activeColumn() {
      return this.columns.find(c => c.name == this.selectedCategory);
    },
    mainColumn() {
      if (!this.main) return this.columns[0];
      return this.columns.find(
        c => c.name.toLowerCase() === this.main.toLowerCase()
      );
    }
  },
  methods: {
    getSlotName(column, index) {
      if (column.name)
        return column.name
          .toLowerCase()
          .split(" ")
          .join("-");
      return index + 1;
    },
    getFieldValue(item, column) {
      const value = item[column.field];

      if (column.transform) return column.transform(value, item);

      return item[column.field] ?? column.default ?? "-";
    },
    isDisabled(item, column) {
      if (typeof column.disabled == "function") return column.disabled(item);

      return column.disabled;
    },
    getOptions(item, column) {
      if (typeof column.options == "function") return column.options(item);

      return column.options || [];
    },
    toggleRow(index) {
      if (this.currentIndex === index) this.currentIndex = undefined;
      else this.currentIndex = index;
    },

    clickOption(option, item) {
      if (option.dialog) {
        const dialog = option.dialog;
        if (dialog.action) this.dialogAction = dialog.action;
        if (dialog.secondaryAction)
          this.dialogSecondaryAction = dialog.secondaryAction;

        delete dialog.action;
        delete dialog.secondaryAction;

        this.dialogProps = { ...dialog };
        this.dialogItem = item;

        this.dialog = true;
      } else return option.click(item);
    },

    onDialogAction() {
      if (this.dialogAction) this.dialogAction(this.dialogItem);
      this.dialog = false;
    },
    onDialogSecondaryAction() {
      if (this.dialogSecondaryAction)
        this.dialogSecondaryAction(this.dialogItem);

      this.dialog = false;
    },
    clearDialog() {
      this.dialogItem = undefined;
      this.dialogAction = undefined;
      this.dialogSecondaryAction = undefined;
      this.dialogProps = {};
    }
  },
  watch: {
    dialog(v) {
      if (!v) setTimeout(this.clearDialog, 300);
    },
    loading() {}
  }
};
</script>

<style lang="scss" scoped>
.snow-data-table {
  .blur {
    filter: blur(6px);
    user-select: none;
  }
  .snow-desktop-table {
    display: grid;
    align-items: center;
    transition: 0.2s all;

    > div {
      overflow: hidden;
    }

    &.table-row {
      .snow-data-table-dropdown {
        opacity: 0;
        cursor: none;
        transition: 0.2s all ease-out;
      }
      &:hover {
        background: var(--dark-4);
        .snow-data-table-dropdown {
          opacity: 1;
          cursor: pointer;
        }
      }
    }
  }

  .snow-responsive-table {
    gap: 16px;
    display: flex;
    flex-direction: column;

    .snow-responsive-table-data-header {
      display: grid;
      grid-template-columns: 1fr auto auto;
      align-items: start;
      gap: 16px;
    }
  }

  .no-content {
    display: flex;
    width: 100%;
    margin-top: 100px;
    max-width: 380px;
    align-items: center;
    flex-direction: column;
  }
}
</style>
